<template>
   <dt class="sr-only">{{ field.text }}</dt>
   <dd>
      <DefaultButton styleType="outlined" :color="Theme.Danger" data-cy="account-delete-btn" @click="deleteAccount">
         {{ field.text }}
      </DefaultButton>
   </dd>
</template>

<script setup lang="ts">
import { useRouteLocale } from "@/composables/useRouteLocale";
import { intercomKey } from "@/plugins/useIntercom";
import { useToastKey } from "@/plugins/useToast";
import { useUserStore } from "@/stores/userStore";
import UsersApi from "@/data/api-layer/usersApi";
import { useBaseContentStore } from "@/stores/baseContentStore";
import { Theme } from "@/types";
import type { AccountOtherField } from "./accountTypes";

const intercom = inject(intercomKey);
const useToast = inject(useToastKey);
const $t = useI18n().t;
const userStore = useUserStore();
const { localePath } = useRouteLocale();
const baseContentStore = useBaseContentStore();

// Props
// -----------------------------------------
const props = defineProps<{
   field: AccountOtherField;
}>();

// Computed
// -----------------------------------------
const organisationInfo = computed(() => {
   if (!baseContentStore.content) return null;
   return baseContentStore.content.organisationInfo;
});

// Methods
// -----------------------------------------
async function deleteAccount() {
   const warningSvg = `
      <div class="flex-shrink-0 w-3.5 h-3.5 mr-2 mt-1">
         <svg class="block fill-error-red" viewBox="0 0 14 14">
               <path
                  d="M7 0C3.136 0 0 3.136 0 7C0 10.864 3.136 14 7 14C10.864 14 14 10.864 14 7C14 3.136 10.864 0 7 0ZM7.7 10.5H6.3V9.1H7.7V10.5ZM7.7 7.7H6.3V3.5H7.7V7.7Z"
                  stroke-width="0"
               />
         </svg>
      </div>
   `;
   // Show a warning before deleting all data
   const accountDelSwal = await useToast({
      title: $t("account.account_delete.modal.title"),
      position: "center",
      padding: "1.25rem 0.5rem 2.5rem 0.5rem",
      width: 592,
      html: `
         <div class="text-left" data-cy="delete-account-modal-content">
            <p class="text-lighter mb-6">${$t("account.account_delete.modal.text", {
               organisation: organisationInfo.value.name,
            })}</p>

            <ul>
               <li class="flex items-start mb-2">
                  ${warningSvg}
                  <div>${$t("account.account_delete.modal.consequences.login", {
                     organisation: organisationInfo.value.name,
                  })}</div>
               </li>
               <li class="flex items-start mb-2">
                  ${warningSvg}
                  <div>${$t("account.account_delete.modal.consequences.studies", {
                     organisation: organisationInfo.value.name,
                     privacyPolicy: `<a href="/privacy" class="underline" target="_blank">
                        ${$t("account.withdrawal.step1.privacy_policy")}
                     </a>`,
                  })}</div>
               </li>
               <li class="flex items-start mb-2">
                  ${warningSvg}
                  <div>${$t("account.account_delete.modal.consequences.personal_data", {
                     organisation: organisationInfo.value.name,
                  })}</div>
               </li>
               <li class="flex items-start mb-2">
                  ${warningSvg}
                  <div>${$t("account.account_delete.modal.consequences.mailing_list", {
                     organisation: organisationInfo.value.name,
                  })}</div>
               </li>
            </ul>
         </div>
      `,
      showConfirmButton: false, // don't use the confirm button (we want the deny warning button styling)
      showDenyButton: true,
      denyButtonText: $t("account.account_delete.button_delete"),
      showCancelButton: true,
      showLoaderOnDeny: true,
      // On click of Deny button > Delete the account and logout

      preDeny: async () => {
         await UsersApi.deleteAccount();
         await userStore.logout();
         intercom.close();
      },
   });
   // force reload update so as to clear all app data
   if (accountDelSwal.isDenied) {
      window.location.href = `${localePath.value}/?account_deleted`;
   }
}
</script>
